// Turns rows pasted from Excel (TSV) into OFA group_settings records, with per-cell validation.

export const OFA_PASTE_COLUMNS = [
    { key: 'group_id', label: 'Gruppo', kind: 'group', required: true, hint: 'ID o nome del gruppo da configurare', example: 'Medicina 2026', aliases: ['group id', 'group', 'gruppo', 'id gruppo'] },
    { key: 'esse3_test_id', label: 'Esse3 Test ID', hint: 'ID del test su Esse3', example: '12345', aliases: ['esse3 test id', 'test id', 'esse3 test'] },
    { key: 'esse3_aa_id', label: 'Esse3 AA ID', hint: 'ID anno accademico su Esse3', example: '2026', aliases: ['esse3 aa id', 'aa id', 'esse3 aa', 'anno accademico'] },
    { key: 'video_completion_group_id', label: 'Video group', kind: 'group', hint: 'ID o nome del gruppo assegnato a fine video', example: 'Medicina video', aliases: ['video group', 'video completion group id', 'video completion group', 'gruppo video'] },
    { key: 'retry_exam_group_id', label: 'Retry group', kind: 'group', hint: 'ID o nome del gruppo per ripetere l\'esame', example: 'Medicina retry', aliases: ['retry group', 'retry exam group id', 'retry exam group', 'gruppo retry'] },
    { key: 'subjects', label: 'Materie', kind: 'subjects', hint: 'ID o nomi delle materie, separati da virgola o punto e virgola', example: 'Biologia; Chimica', aliases: ['materie', 'materia', 'subjects', 'subject'] },
]

const norm = (s) => `${s ?? ''}`.trim().toLowerCase().replace(/[_\s]+/g, ' ')

// A group can have several rows, one per Esse3 exam/year: the row identity is group + test + year.
const rowKey = (g) => [g.group_id, g.esse3_test_id, g.esse3_aa_id].map(v => `${v ?? ''}`.trim()).join('|')

// Excel copies cells as TSV; cells containing tabs/newlines come wrapped in quotes.
export function parseTsv(text){
    const rows = [[]]
    let cell = '', quoted = false
    for(let i = 0; i < text.length; i++){
        const c = text[i]
        if(quoted){
            if(c === '"'){
                if(text[i+1] === '"'){ cell += '"'; i++ } else quoted = false
            } else cell += c
        }
        else if(c === '"' && cell === '') quoted = true
        else if(c === '\t'){ rows[rows.length-1].push(cell); cell = '' }
        else if(c === '\n'){ rows[rows.length-1].push(cell); cell = ''; rows.push([]) }
        else if(c !== '\r') cell += c
    }
    rows[rows.length-1].push(cell)
    return rows.map(r => r.map(c => c.trim())).filter(r => r.some(Boolean))
}

// Returns the column order from a header row, or null if the row isn't a header.
function detectHeader(row){
    const keys = row.map(cell => {
        if(!cell) return null
        const col = OFA_PASTE_COLUMNS.find(c => norm(c.key) === norm(cell) || c.aliases.includes(norm(cell)))
        return col ? col.key : undefined
    })
    return keys.some(k => k) && !keys.includes(undefined) ? keys : null
}

// Lookups accept either the ID or the (case-insensitive) name. Duplicate names map to null = ambiguous.
function buildIndex(items, idKey, nameKey){
    const byId = new Map(), byName = new Map()
    for(const it of items){
        byId.set(`${it[idKey]}`, it)
        const n = norm(it[nameKey])
        if(n) byName.set(n, byName.has(n) ? null : it)
    }
    return (value) => {
        if(byId.has(value)) return { item: byId.get(value) }
        const n = norm(value)
        if(!byName.has(n)) return { error: `"${value}" non trovato` }
        const item = byName.get(n)
        return item ? { item } : { error: `"${value}" ambiguo (più elementi con lo stesso nome)` }
    }
}

export function parseOfaPaste(text, { groupsById = {}, subjects = [], existing = [] } = {}){
    const table = parseTsv(text || '')
    if(!table.length) return { hasHeader: false, rows: [] }

    const header = detectHeader(table[0])
    const order = header || OFA_PASTE_COLUMNS.map(c => c.key)
    const dataRows = header ? table.slice(1) : table

    const findGroup = buildIndex(Object.entries(groupsById).map(([group_id, name]) => ({ group_id, name })), 'group_id', 'name')
    const findSubject = buildIndex(subjects, 'subject_id', 'subject_name')
    const existingKeys = new Set(existing.map(rowKey))
    const seen = new Set()

    const rows = dataRows.map((cells, i) => {
        const raw = {}
        order.forEach((key, idx) => { if(key) raw[key] = cells[idx] || '' })

        const record = { group_id: '', esse3_test_id: '', esse3_aa_id: '', subjects: [], video_completion_group_id: '', retry_exam_group_id: '' }
        const errors = {}

        for(const col of OFA_PASTE_COLUMNS){
            const value = raw[col.key] || ''
            if(!value){
                if(col.required) errors[col.key] = 'Obbligatorio'
                continue
            }
            if(col.kind === 'group'){
                const { item, error } = findGroup(value)
                if(error) errors[col.key] = error
                record[col.key] = item ? item.group_id : value
            } else if(col.kind === 'subjects'){
                const missing = []
                for(const part of value.split(/[,;\n]/).map(s => s.trim()).filter(Boolean)){
                    const { item, error } = findSubject(part)
                    if(error){ missing.push(error); continue }
                    if(!record.subjects.some(s => `${s.subject_id}` === `${item.subject_id}`)){
                        record.subjects.push({ subject_id: item.subject_id, subject_name: item.subject_name })
                    }
                }
                if(missing.length) errors.subjects = missing.join(', ')
            } else {
                record[col.key] = value
            }
        }

        if(record.group_id && !errors.group_id){
            if(seen.has(rowKey(record))) errors.group_id = 'Riga duplicata nell\'incolla'
            seen.add(rowKey(record))
        }

        const valid = !Object.keys(errors).length
        const status = !valid ? 'invalid' : existingKeys.has(rowKey(record)) ? 'update' : 'new'
        return { line: i + 1 + (header ? 1 : 0), raw, record, errors, status }
    })

    return { hasHeader: !!header, rows }
}

// Valid records update the existing row with the same group + test + year, the rest are appended.
// Fields not handled by the paste (e.g. esse3_libretto_row) are kept from the existing row.
export function mergeGroupSettings(existing = [], records = []){
    const next = [...existing]
    for(const rec of records){
        const idx = next.findIndex(g => rowKey(g) === rowKey(rec))
        if(idx >= 0) next[idx] = { ...next[idx], ...rec }
        else next.push(rec)
    }
    return next
}
